import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { InlineMarkdown } from "./InlineMarkdown"
import { 
  CalendarIcon, RefreshCw, CheckCircle2, AlertCircle
} from "lucide-react"
import { cn } from "@/lib/utils"
import { format } from "date-fns"
import { DateRange } from "react-day-picker"
import { DATE_PRESETS } from "../../utils/constants"

interface DashboardAgent {
  id: number
  title: string | null
}

interface DashboardControlsProps {
  // Date range
  dateRange: DateRange
  onDateRangeChange: (range: DateRange) => void

  // Instant filters
  messageDirectionFilter: 'all' | 'incoming' | 'outgoing'
  onMessageDirectionFilterChange: (value: 'all' | 'incoming' | 'outgoing') => void
  handledByFilter: 'all' | 'bot' | 'human'
  onHandledByFilterChange: (value: 'all' | 'bot' | 'human') => void
  escalationFilter: 'all' | 'escalated' | 'resolved' | 'non_escalated'
  onEscalationFilterChange: (value: 'all' | 'escalated' | 'resolved' | 'non_escalated') => void
  contactSourceFilter: 'real_only' | 'synthetic_only' | 'all'
  onContactSourceFilterChange: (value: 'real_only' | 'synthetic_only' | 'all') => void
  agents?: DashboardAgent[]
  agentFilter?: string
  onAgentFilterChange?: (value: string) => void
  channels?: string[]
  channelFilter?: string
  onChannelFilterChange?: (value: string) => void

  // Loading/Error
  loading: boolean
  loadingProgress: string
  error?: string
}

export function DashboardControls({
  dateRange,
  onDateRangeChange,
  messageDirectionFilter,
  onMessageDirectionFilterChange,
  handledByFilter,
  onHandledByFilterChange,
  escalationFilter,
  onEscalationFilterChange,
  contactSourceFilter,
  onContactSourceFilterChange,
  agents = [],
  agentFilter = 'all',
  onAgentFilterChange,
  channels = [],
  channelFilter = 'all',
  onChannelFilterChange,
  loading,
  loadingProgress,
  error,
}: DashboardControlsProps) {
  const timeValue = (d?: Date) =>
    d ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : ""

  const hasCustomTime = (d: Date, edge: "from" | "to") =>
    edge === "from" ? !(d.getHours() === 0 && d.getMinutes() === 0)
      : !(d.getHours() === 23 && d.getMinutes() === 59)

  const withTime = (base: Date, hhmm: string) => {
    const [h, m] = hhmm.split(":").map(Number)
    const next = new Date(base)
    next.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0)
    return next
  }

  const handleCalendarSelect = (range: DateRange) => {
    const next: DateRange = { ...range }
    if (next.from && dateRange?.from) next.from = withTime(next.from, timeValue(dateRange.from))
    if (next.to && dateRange?.to) next.to = withTime(next.to, timeValue(dateRange.to))
    else if (next.to) next.to = withTime(next.to, "23:59")
    onDateRangeChange(next)
  }

  const setFromTime = (hhmm: string) => {
    if (!dateRange?.from) return
    onDateRangeChange({ ...dateRange, from: withTime(dateRange.from, hhmm) })
  }
  const setToTime = (hhmm: string) => {
    if (!dateRange?.to) return
    onDateRangeChange({ ...dateRange, to: withTime(dateRange.to, hhmm) })
  }

  return (
    <Card className="border-2 border-dashed">
      <CardContent className="pt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
          <div>
            <Label htmlFor="direction-filter" className="text-xs text-muted-foreground">Direction</Label>
            <Select
              value={messageDirectionFilter}
              onValueChange={(v) => onMessageDirectionFilterChange(v as 'all' | 'incoming' | 'outgoing')}
            >
              <SelectTrigger id="direction-filter" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="incoming">Incoming only</SelectItem>
                <SelectItem value="outgoing">Outgoing only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="handled-by-filter" className="text-xs text-muted-foreground">Handled By</Label>
            <Select
              value={handledByFilter}
              onValueChange={(v) => onHandledByFilterChange(v as 'all' | 'bot' | 'human')}
            >
              <SelectTrigger id="handled-by-filter" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="bot">Bot</SelectItem>
                <SelectItem value="human">Human</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="escalation-filter" className="text-xs text-muted-foreground">Escalation</Label>
            <Select
              value={escalationFilter}
              onValueChange={(v) => onEscalationFilterChange(v as 'all' | 'escalated' | 'resolved' | 'non_escalated')}
            >
              <SelectTrigger id="escalation-filter" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="escalated">Escalated only</SelectItem>
                <SelectItem value="resolved">Resolved only</SelectItem>
                <SelectItem value="non_escalated">Non-escalated only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="contact-source-filter" className="text-xs text-muted-foreground">Conversation Source</Label>
            <Select
              value={contactSourceFilter}
              onValueChange={(v) => onContactSourceFilterChange(v as 'real_only' | 'synthetic_only' | 'all')}
            >
              <SelectTrigger id="contact-source-filter" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="real_only">Real Conversations</SelectItem>
                <SelectItem value="synthetic_only">Simulated Conversations</SelectItem>
                <SelectItem value="all">All Conversations</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {agents.length > 0 && onAgentFilterChange && (
            <div>
              <Label htmlFor="agent-filter" className="text-xs text-muted-foreground">Agent</Label>
              <Select
                value={agentFilter}
                onValueChange={onAgentFilterChange}
              >
                <SelectTrigger id="agent-filter" className="h-9">
                  {/* Render the selected agent's title with inline markdown rather
                      than relying on SelectValue's plain-text echo. */}
                  {(() => {
                    if (agentFilter === 'all') return <span>All Agents</span>
                    const selected = agents.find((a) => String(a.id) === agentFilter)
                    const title = selected?.title || `Agent ${agentFilter}`
                    return <InlineMarkdown className="truncate">{title}</InlineMarkdown>
                  })()}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      <InlineMarkdown>{a.title || `Agent ${a.id}`}</InlineMarkdown>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {channels.length > 1 && onChannelFilterChange && (
            <div>
              <Label htmlFor="channel-filter" className="text-xs text-muted-foreground">Channel</Label>
              <Select value={channelFilter} onValueChange={onChannelFilterChange}>
                <SelectTrigger id="channel-filter" className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  {channels.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="lg:col-span-2">
            <Label className="text-xs text-muted-foreground">Date Range</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal h-9",
                    !dateRange && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {dateRange?.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, hasCustomTime(dateRange.from, "from") ? "MMM dd, yyyy HH:mm" : "MMM dd, yyyy")}
                          {" - "}
                          {format(dateRange.to, hasCustomTime(dateRange.to, "to") ? "MMM dd, yyyy HH:mm" : "MMM dd, yyyy")}
                        </>
                      ) : (
                        format(dateRange.from, hasCustomTime(dateRange.from, "from") ? "MMM dd, yyyy HH:mm" : "MMM dd, yyyy")
                      )
                    ) : (
                      "Pick a date range"
                    )}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="flex">
                  <div className="border-r p-2 space-y-1">
                    {DATE_PRESETS.map((preset) => (
                      <Button
                        key={preset.label}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={() => onDateRangeChange(preset.getValue())}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                  <div>
                    <Calendar
                      autoFocus
                      mode="range"
                      defaultMonth={dateRange?.from}
                      selected={dateRange}
                      onSelect={(range) => range && handleCalendarSelect(range)}
                      numberOfMonths={2}
                    />
                    <div className="flex items-center gap-4 border-t p-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground w-10">From</Label>
                        <Input
                          type="time"
                          className="h-8 w-[120px]"
                          value={timeValue(dateRange?.from)}
                          disabled={!dateRange?.from}
                          onChange={(e) => setFromTime(e.target.value)}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground w-6">To</Label>
                        <Input
                          type="time"
                          className="h-8 w-[120px]"
                          value={timeValue(dateRange?.to)}
                          disabled={!dateRange?.to}
                          onChange={(e) => setToTime(e.target.value)}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground ml-auto">Times are local</span>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Loading Progress */}
        {loading && (
          <div className="mt-4 text-sm text-blue-600 flex items-center gap-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            {loadingProgress || 'Refreshing data...'}
          </div>
        )}
        {loadingProgress && !loading && (
          <div className="mt-3 text-sm text-green-600 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {loadingProgress}
          </div>
        )}
        {error && (
          <Alert className="mt-4" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
